import serverVars from 'server-vars';
import { type EcomProduct } from '../types.ts';
import { type ElevarClickProduct, type ElevarImpression, type ElevarProduct } from './types.ts';
import {
    compact,
    toStr,
    toRequiredStr,
    toNumericStr,
    getCategory,
    resolveProductCategory,
    type TrackingValue,
} from './helpers.ts';
import { getElevarCurrencyCode } from './userProperties.ts';

const getLocaleOrigin = (): string => toStr(serverVars.get('localeOrigin')) ?? '';

type ConvertedAmountEntry = { currency?: string | null; amount?: number | null };
type ConvertedAmountMap = Record<string, number | null | undefined>;

// GTM tracks item prices in USD, Elevar needs the buyer's currency. Depending on the query, items
// carry the conversions either as a currency-keyed map or as a list of entries.
const getLocalizedAmount = (converted: EcomProduct['convertedAmounts']): number | undefined => {
    if (!converted) {
        return undefined;
    }
    const currency = getElevarCurrencyCode();
    const amount = Array.isArray(converted)
        ? (converted as ConvertedAmountEntry[]).find(entry => entry?.currency === currency)?.amount
        : (converted as ConvertedAmountMap)[currency];
    return typeof amount === 'number' ? amount : undefined;
};

const getLocalizedPrice = (product: EcomProduct): TrackingValue =>
    getLocalizedAmount(product.convertedAmounts) ??
    getLocalizedAmount(product.convertedAmountList) ??
    product.price;

export type ElevarProductContext =
    | 'cart_contents'
    | 'view_item'
    | 'add_to_cart'
    | 'remove_from_cart'
    | 'select_item'
    | 'checkout'
    | 'purchase';

export type ElevarImpressionContext = 'list' | 'view_cart';

type ProductBase = {
    id: string;
    name: string;
    brand?: string;
    category: string;
    price: string;
    compare_at_price: string;
    product_id: string;
    variant_id: string;
    variant: string;
    selling_plan_name: string;
};

const buildBase = (product: EcomProduct): ProductBase => {
    const id = toRequiredStr(product.id);
    return {
        id,
        name: toRequiredStr(product.name),
        category: getCategory(product),
        price: toRequiredStr(getLocalizedPrice(product), '0.0'),
        compare_at_price: toRequiredStr(product.compare_at_price, '0.0'),
        brand: toStr(product.brand),
        // Elevar requires these fields; we do not use variants or selling plans — send static placeholders.
        product_id: '1',
        variant_id: '1',
        variant: 'NA',
        selling_plan_name: 'one-time',
    };
};

const toPosition = (product: EcomProduct, index: number): number =>
    product.position && product.position > 0 ? product.position : index + 1;

const getProductUrl = (product: EcomProduct): string | undefined => {
    const path = toStr(product.url);
    if (!path) {
        return undefined;
    }
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    const origin = getLocaleOrigin();
    if (!origin) {
        return path;
    }
    return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
};

const LIST_REQUIRED_PRODUCT_CONTEXTS = new Set<ElevarProductContext>([
    'view_item',
    'select_item',
    'add_to_cart',
    'remove_from_cart',
    'checkout',
    'purchase',
    'cart_contents',
]);

const CHECKOUT_LIKE_CONTEXTS = new Set<ElevarProductContext>([
    'checkout',
    'purchase',
    'cart_contents',
]);

const getMappedList = (
    context: ElevarProductContext,
    list: string | undefined
): { list: string } | Record<string, never> => {
    if (LIST_REQUIRED_PRODUCT_CONTEXTS.has(context)) {
        return { list: list ?? '' };
    }
    return list ? { list } : {};
};

const getMappedImage = (
    context: ElevarProductContext,
    image: string | undefined
): { image: string } | Record<string, never> => {
    if (
        context === 'add_to_cart' ||
        context === 'remove_from_cart' ||
        CHECKOUT_LIKE_CONTEXTS.has(context)
    ) {
        return { image: image ?? '' };
    }
    return image ? { image } : {};
};

const getMappedUrl = (
    context: ElevarProductContext,
    productUrl: string | undefined
): { url: string } | Record<string, never> => {
    if (
        context === 'add_to_cart' ||
        context === 'remove_from_cart' ||
        CHECKOUT_LIKE_CONTEXTS.has(context)
    ) {
        return { url: productUrl ?? '' };
    }
    return productUrl ? { url: productUrl } : {};
};

const mapProduct = (
    product: EcomProduct,
    index: number,
    context: ElevarProductContext
): ElevarProduct | ElevarClickProduct => {
    const base = buildBase(product);
    const productUrl = getProductUrl(product);
    const image = toStr(product.image);
    const list = resolveProductCategory(product);

    const shared = compact({
        ...base,
        ...getMappedList(context, list),
        ...getMappedImage(context, image),
        ...getMappedUrl(context, productUrl),
        ...(!['view_item', 'select_item'].includes(context)
            ? { quantity: toNumericStr(product.quantity) || '1' }
            : {}),
        ...(['checkout', 'purchase', 'cart_contents'].includes(context)
            ? { position: String(product.position || index + 1) }
            : {}),
    });

    if (context === 'select_item') {
        return { ...shared, position: toPosition(product, index) } as ElevarClickProduct;
    }

    return shared as ElevarProduct;
};

export const mapProducts = (
    products: ReadonlyArray<EcomProduct> | null | undefined,
    context: ElevarProductContext
): ElevarProduct[] =>
    (products || []).map((product, index) => mapProduct(product, index, context) as ElevarProduct);

export const mapClickProducts = (
    products: ReadonlyArray<EcomProduct> | null | undefined
): ElevarClickProduct[] =>
    (products || []).map(
        (product, index) => mapProduct(product, index, 'select_item') as ElevarClickProduct
    );

const mapImpression = (
    product: EcomProduct,
    index: number,
    context: ElevarImpressionContext
): ElevarImpression => {
    const base = buildBase(product);
    const list = resolveProductCategory(product);
    return compact({
        id: base.id,
        name: base.name,
        category: base.category,
        price: base.price,
        compare_at_price: base.compare_at_price,
        product_id: base.product_id,
        variant_id: base.variant_id,
        variant: base.variant,
        selling_plan_name: base.selling_plan_name,
        position: toPosition(product, index),
        brand: base.brand,
        ...(context !== 'view_cart' ? { list: list ?? '' } : {}),
        ...(context === 'view_cart' ? { quantity: toNumericStr(product.quantity) || '1' } : {}),
    }) as ElevarImpression;
};

export const mapImpressions = (
    products: ReadonlyArray<EcomProduct> | null | undefined,
    context: ElevarImpressionContext = 'list'
): ElevarImpression[] =>
    (products || []).map((product, index) => mapImpression(product, index, context));
